import { CurrentUser } from "@/api/types";
import { UserHasAnyPermissions } from "@/app/_helpers/permissions";
import { useShallowEqualSelector } from "@/app/_helpers/redux";

export function useAppPermissions() {
  const { isAuthenticated, currentUser } = useShallowEqualSelector((state) => ({
    isAuthenticated: state.auth.isAuthenticated,
    currentUser: state.auth.currentUser,
  }));

  const currentUserClinician = useShallowEqualSelector(() => {
    if (currentUser?.clinician) {
      return currentUser?.clinician;
    }
    return null;
  });

  const {
    hasMyConsultsPermissions,
    hasTeamPermissions,
    hasTimeOffPermissions,
    hasMyClientsPermissions,
    hasConsentSurveyPermissions,
    hasGroupTherapyPermissions,
    hasViewClinicianMetricsEnabled,
  } = getPermissions(currentUser);

  return {
    isAuthenticated,
    currentUserClinician,
    hasMyConsultsPermissions,
    hasTeamPermissions,
    hasTimeOffPermissions,
    hasMyClientsPermissions,
    hasConsentSurveyPermissions,
    hasGroupTherapyPermissions,
    hasViewClinicianMetricsEnabled,
  };
}

function getPermissions(currentUser: CurrentUser | null) {
  const hasMyConsultsPermissions = UserHasAnyPermissions(currentUser, [
    "IsSuperUser",
    "IsConsultClinician",
    "IsRequeuer",
    "IsPsychiatrist",
  ]);

  const hasTimeOffPermissions = UserHasAnyPermissions(currentUser, [
    "IsSuperUser",
    "IsMatchingAdmin",
    "IsClinicalLeader",
    "IsCareCoordinator",
  ]);

  const hasTeamPermissions = UserHasAnyPermissions(currentUser, [
    "IsSuperUser",
    "IsMatchingAdmin",
    "IsClinicalLeader",
  ]);

  const hasMyClientsPermissions = UserHasAnyPermissions(currentUser, [
    "IsMyClientsWriteAllowed",
    "IsClinician",
    "IsClinicalLeader",
    "IsSuperUser",
    "IsCareCoordinator",
  ]);

  const hasConsentSurveyPermissions = UserHasAnyPermissions(currentUser, [
    "IsSuperUser",
    "IsCouplesClinician",
  ]);

  const hasGroupTherapyPermissions = UserHasAnyPermissions(currentUser, [
    "IsGroupTherapyUser",
    "IsSuperUser",
  ]);

  const hasViewClinicianMetricsEnabled = UserHasAnyPermissions(currentUser, [
    "IsClinician",
    "IsClinicalLeader",
    "IsSuperUser",
    "IsCareCoordinator",
  ]);

  return {
    hasMyConsultsPermissions,
    hasTimeOffPermissions,
    hasTeamPermissions,
    hasMyClientsPermissions,
    hasConsentSurveyPermissions,
    hasGroupTherapyPermissions,
    hasViewClinicianMetricsEnabled,
  };
}
